import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const headingText = children => String(Array.isArray(children) ? children.join('') : children);
const headingId = children => {
  const text = headingText(children);
  if (text === 'AHA Commercial Beta Terms of Service and Privacy Notice') return 'terms';
  if (text === 'Privacy Notice') return 'privacy';
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
};

export default function LegalPage() {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/legal/document`)
      .then(async response => {
        if (!response.ok) throw new Error('The approved legal document is unavailable.');
        return response.text();
      })
      .then(text => active && setMarkdown(text))
      .catch(err => active && setError(err.message));
    return () => { active = false; };
  }, []);

  return (
    <main className="min-h-screen bg-background" data-testid="legal-page">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <span className="font-semibold" data-testid="legal-page-title">AHA Legal</span>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/signup" data-testid="legal-back-to-signup-link"><ArrowLeft className="mr-2 h-4 w-4" />Back to signup</Link>
          </Button>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
        {error && <Alert variant="destructive" data-testid="legal-page-error"><AlertDescription>{error}</AlertDescription></Alert>}
        {!error && !markdown && (
          <div className="flex items-center gap-2 text-muted-foreground" data-testid="legal-page-loading">
            <Loader2 className="h-4 w-4 animate-spin" />Loading approved legal text…
          </div>
        )}
        {markdown && (
          <article className="legal-document" data-testid="legal-document-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              h1: ({ children }) => <h1 id={headingId(children)}>{children}</h1>,
              h2: ({ children }) => <h2 id={headingId(children)}>{children}</h2>,
              a: ({ href, children }) => <a href={href} target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noreferrer' : undefined}>{children}</a>,
            }}>{markdown}</ReactMarkdown>
          </article>
        )}
      </div>
    </main>
  );
}